import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import RedirectImg from '../../assets/icons/redirect-icon.svg';
import BackArrowImg from '../../assets/icons/back-arrow-icon.svg';

import { selectData } from 'reducers/answers';
import {
  selectHistory,
  selectIsMobile,
  selectSelectedAnswerIndex,
} from 'reducers/main';
import {
  addScreenReaderNotification,
  selectSelectedArticle,
} from 'reducers/workflows';
import { styled } from '@mui/material/styles';
import { appendAnalyticsParamsToURL } from 'components/Article/appendAnalyticsParamsToURL';
import { navigateBack } from 'actions/main';
import { Typography } from '@forethought-technologies/forethought-elements';
import { setSelectedArticle } from 'actions/workflows/workflows';
import sanitizeHtml from 'utils/sanitizeHtml';
import { isValidUrl } from 'utils/utils';

export const ArticlePage: React.FC<React.PropsWithChildren<unknown>> = () => {
  const dispatch = useAppDispatch();

  // Selectors
  const answerData = useAppSelector(selectData);
  const selectedAnswerIndex = useAppSelector(selectSelectedAnswerIndex);
  const selectedArticle = useAppSelector(selectSelectedArticle);
  const history = useAppSelector(selectHistory);
  const isMobile = useAppSelector(selectIsMobile);

  const [isTitleVisibleInHeader, setIsTitleVisibleInHeader] = useState(false);

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);

  // Consts
  const answer = answerData[selectedAnswerIndex] || selectedArticle;

  // scroll to top when article is mounted
  useEffect(() => {
    containerRef.current?.scrollIntoView();
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    const onScroll = (e: Event) => {
      const scrollPosition = (e.target as HTMLDivElement).scrollTop;
      setIsTitleVisibleInHeader(scrollPosition > 50);
    };
    container?.addEventListener('scroll', onScroll);

    return () => container?.removeEventListener('scroll', onScroll);
  }, []);

  const deSelectArticle = () => {
    if (history.length > 1) {
      dispatch(setSelectedArticle(undefined));
      dispatch(navigateBack());
      dispatch(addScreenReaderNotification('Article closed'));
    }
  };

  if (!answer?.full_html) return null;

  return (
    <Container className='forethought-scrollbar' ref={containerRef}>
      <Header>
        <BackArrowButton
          aria-label='Return to chat'
          onClick={() => deSelectArticle()}
          type='button'
        />
        {isTitleVisibleInHeader && (
          <HeaderTitleContainer isMobile={isMobile}>
            <Typography variant='font14Bold'>{answer.title}</Typography>
          </HeaderTitleContainer>
        )}
        {answer.link && isValidUrl(answer.link) && (
          <a
            href={appendAnalyticsParamsToURL(answer.link)}
            onClick={() => deSelectArticle()}
            rel='noopener noreferrer'
            target='_blank'
          >
            <RedirectIcon alt='Open article in a new tab' src={RedirectImg} />
          </a>
        )}
      </Header>
      <AnswerContainer>
        <h1
          // eslint-disable-next-line react/forbid-dom-props
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(answer.title) }}
        />
        <div
          // eslint-disable-next-line react/forbid-dom-props
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(answer.full_html),
          }}
        />
      </AnswerContainer>
    </Container>
  );
};

const Container = styled('div')`
  background-color: ${props => props.theme.palette.colors.white};
  display: flex;
  flex-direction: column;
  height: 100%;
  line-height: 1.3;
  position: relative;
  overflow-y: scroll;
  word-wrap: break-word;
  gap: 18px;
  width: 100%;
`;

const AnswerContainer = styled('div')`
  background-color: ${props => props.theme.palette.colors.white};
  margin-bottom: 72px;
  margin-top: -4px;
  padding-left: 16px;
  padding-right: 16px;

  h1 {
    font-size: 19px;
  }

  h2 {
    font-size: 18px;
  }

  h3 {
    font-size: 17px;
  }

  h4,
  h5,
  h6 {
    font-size: 16px;
  }

  :first-child {
    margin-top: 0;
  }

  a {
    color: var(--message-link-color);
    word-break: break-word;
  }

  a:visited {
    color: ${props => props.theme.palette.colors.purple[600]};
  }

  a:hover {
    color: ${props => props.theme.palette.colors.purple[600]};
    text-decoration: none;
  }

  a:active {
    color: ${props => props.theme.palette.colors.purple[700]};
  }

  img {
    max-width: 100% !important;
    height: auto !important;
    width: initial !important;
  }

  iframe {
    height: auto;
    width: 100%;
  }

  video {
    height: auto;
    width: 100%;
  }

  pre {
    overflow-x: auto;
    width: 333px;
  }

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-track-piece {
    background-color: #f0f1f4;
    border-radius: 5px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 5px;
    box-shadow: inset 0 0 6px #cfd4dd;
    box-shadow: inset 0 0 6px #cfd4dd;
  }

  ol,
  ul {
    padding-left: 40px;
  }
`;

const Header = styled('div')`
  border-bottom: 1px solid ${props => props.theme.palette.colors.grey[300]};
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 16px 18px 16px;
  box-sizing: border-box;
  background-color: ${props => props.theme.palette.colors.white};
  position: sticky;
  text-align: center;
  top: 0;
  z-index: 3;
`;

const RedirectIcon = styled('img')`
  width: 15px;
`;

export const BackArrowButton = styled('button')`
  width: 20px;
  height: 20px;
  cursor: pointer;
  background-color: transparent;
  background-image: url(${BackArrowImg});
  border: none;
`;

const HeaderTitleContainer = styled('div')<{ isMobile?: boolean }>`
  width: ${props => (props.isMobile ? '215' : '270')}px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  margin: 0 20px;
`;
